// ─────────────────────────────────────────────────────────────
//  Мелкие примитивы оформления приложения: стеклянная карточка, знак-луна,
//  кнопка-пилюля, разделитель и формат денег.
//
//  Раньше они жили внутри main.jsx. Вынесены сюда, чтобы экраны из
//  components/ (например «Тарифы») выглядели ровно так же, а не повторяли
//  ту же разметку своими руками. Стили у всех классов глобальные (styles.css).
// ─────────────────────────────────────────────────────────────
import { useId } from 'react'
import { Sparkles } from 'lucide-react'

export function Glass({ className = '', children }) {
  return <div className={`glass ${className}`}>{children}</div>
}

// Знак Moonly: светящаяся сиреневая стеклянная сфера с полумесяцем — в той же
// манере, что прежний шарик-орб. Вектор, а не картинка: одинаково чёткий от 26px
// в навбаре до 100px на пустых экранах. id градиентов уникальны на каждый экземпляр.
export function Orb({ className = '' }) {
  const id = useId().replace(/:/g, '')
  return (
    <svg className={`orb ${className}`} viewBox="0 0 64 64" aria-hidden="true" focusable="false">
      <defs>
        <radialGradient id={`${id}s`} cx="50%" cy="45%" r="55%">
          <stop offset="0" stopColor="#c7a4ff" />
          <stop offset=".6" stopColor="#9563ea" />
          <stop offset="1" stopColor="#5a2fb8" />
        </radialGradient>
        <radialGradient id={`${id}r`} cx="50%" cy="50%" r="50%">
          <stop offset=".72" stopColor="#fff" stopOpacity="0" />
          <stop offset=".93" stopColor="#f3e6ff" stopOpacity=".55" />
          <stop offset="1" stopColor="#fff" stopOpacity=".9" />
        </radialGradient>
        <linearGradient id={`${id}m`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#fff" />
          <stop offset="1" stopColor="#f1d9ff" />
        </linearGradient>
        <mask id={`${id}c`}>
          <rect width="64" height="64" fill="#fff" />
          <circle cx="39" cy="26" r="13.5" fill="#000" />
        </mask>
        <filter id={`${id}g`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.2" />
        </filter>
      </defs>
      <circle cx="32" cy="32" r="31" fill={`url(#${id}s)`} />
      <circle cx="32" cy="32" r="31" fill={`url(#${id}r)`} />
      <g mask={`url(#${id}c)`}>
        <circle cx="30" cy="33" r="15" fill="#ffd6ff" opacity=".9" filter={`url(#${id}g)`} />
        <circle cx="30" cy="33" r="15" fill={`url(#${id}m)`} />
      </g>
      <ellipse cx="22" cy="14" rx="10" ry="4.5" fill="#fff" fillOpacity=".35" transform="rotate(-30 22 14)" />
      <path d="M46.5 38l1 2.7 2.7 1-2.7 1-1 2.7-1-2.7-2.7-1 2.7-1z" fill="#fff" />
      <circle cx="48" cy="20" r="1.2" fill="#fff" fillOpacity=".9" />
      <circle cx="17" cy="45" r="1" fill="#fff" fillOpacity=".7" />
    </svg>
  )
}

export function Pill({ children, className = '', onClick, type = 'button', disabled = false, ...props }) {
  return (
    <button type={type} className={`pill press ${className}`} onClick={onClick} disabled={disabled} {...props}>
      {children}
    </button>
  )
}

export function SparkLine() {
  return <div className="spark-line"><span /><Sparkles size={24} /><span /></div>
}

/** 1234.50 → «1 235» (в рублях копейки в интерфейсе не показываем). */
export const money = value => new Intl.NumberFormat('ru-RU').format(Math.round(Number(value) || 0))
